// ──────────────────────────────────────────────────────────────────────
// D61 — Content Calendar (per-client only).
//
// REAL month-grid (Notion-style) view. Mon–Sun columns, 6 rows of date
// cells. Each cell shows posts scheduled that day as chips.
//
// Click any date cell  → "New Post" modal pre-filled with that date.
// Click a post chip    → Edit drawer.
// Drawer includes: media upload, GMB platform, target locations,
// comments thread (internal + client), client-share token / copy URL.
//
// Mobile: collapses to a vertical day-list for the current month.
// ──────────────────────────────────────────────────────────────────────
import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Plus, Loader2, Edit3, Trash2, AlertTriangle, MapPin, ChevronLeft, ChevronRight,
  Upload, X, Image as ImgIcon, Film, MessageCircle, Send, Share2, Copy as CopyIcon,
  CheckCircle2, Globe, Mail, UserPlus, Users, RotateCw, Clock,
} from 'lucide-react';
import { contentPostAPI, clientGroupAPI, mediaAPI, clientContactAPI, calendarShareAPI } from '../lib/api';
import useLiveData, { broadcastChange } from '../lib/useLiveData';
import LastSyncedPill from './LastSyncedPill';
import { toast } from 'sonner';

const STATUSES = ['idea', 'draft', 'in_review', 'approved', 'scheduled', 'posted', 'failed'];
const TYPES = ['social_post', 'reel', 'story', 'ad', 'email', 'sms'];
const PLATFORMS = ['IG', 'FB', 'TikTok', 'X', 'YouTube', 'Threads', 'LinkedIn', 'GMB'];
const PLATFORM_LABELS = { IG: 'Instagram', FB: 'Facebook', TikTok: 'TikTok', X: 'X', YouTube: 'YouTube', Threads: 'Threads', LinkedIn: 'LinkedIn', GMB: 'Google My Business' };

const STATUS_COLORS = {
  idea: 'bg-foreground/[0.05] text-muted-foreground/70 border-border/30',
  draft: 'bg-foreground/[0.07] text-foreground/80 border-border/40',
  in_review: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  approved: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  scheduled: 'bg-blue-500/15 text-blue-300 border-blue-500/30',
  posted: 'bg-gold/15 text-gold border-gold/30',
  failed: 'bg-red-500/15 text-red-300 border-red-500/30',
};

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const ymdLocal = (d) => {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

// Build a 6-row × 7-col matrix of dates (Mon-first), starting at the
// Monday on/before the 1st of the visible month.
function buildMonthMatrix(year, month) {
  const first = new Date(year, month, 1);
  // JS getDay: Sun=0..Sat=6. We want Mon=0..Sun=6.
  const offset = (first.getDay() + 6) % 7;
  const start = new Date(year, month, 1 - offset);
  const rows = [];
  for (let r = 0; r < 6; r++) {
    const row = [];
    for (let c = 0; c < 7; c++) {
      const d = new Date(start);
      d.setDate(start.getDate() + r * 7 + c);
      row.push(d);
    }
    rows.push(row);
  }
  return rows;
}

const EMPTY_POST = {
  client_id: '', target_locations: [], scheduled_for: '',
  platforms: [], content_type: 'social_post', caption: '', media: [],
  status: 'idea', tags: [], notes: '',
};

export default function ContentCalendar({ workspaceId, clientId = null, clients = [] }) {
  const today = useMemo(() => new Date(), []);
  const [cursor, setCursor] = useState(() => new Date(today.getFullYear(), today.getMonth(), 1));
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_POST, client_id: clientId || '' });
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [locations, setLocations] = useState([]);
  const [comments, setComments] = useState([]);
  // D64 — track which media items failed to load so we can show a ⚠ Broken badge.
  const [brokenMedia, setBrokenMedia] = useState(() => new Set());
  const markBroken = useCallback((id) => setBrokenMedia(s => { const n = new Set(s); n.add(id); return n; }), []);
  // Reset broken state whenever the editor opens for a different post.
  useEffect(() => { setBrokenMedia(new Set()); }, [editing?.id]);
  // D64 — "Has comments" filter chip (sticky per session).
  const [hasCommentsFilter, setHasCommentsFilter] = useState(() => sessionStorage.getItem('contentcal:hasCommentsFilter') === '1');
  useEffect(() => { sessionStorage.setItem('contentcal:hasCommentsFilter', hasCommentsFilter ? '1' : '0'); }, [hasCommentsFilter]);
  const [commentBody, setCommentBody] = useState('');
  const [shareUrl, setShareUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const monthStartISO = useMemo(() => new Date(cursor.getFullYear(), cursor.getMonth(), 1).toISOString(), [cursor]);
  const monthEndISO = useMemo(() => new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1).toISOString(), [cursor]);

  const { data: posts, loading, lastSynced, refetch } = useLiveData(
    async () => {
      if (!workspaceId || !clientId) return [];
      const r = await contentPostAPI.list(workspaceId, { client_id: clientId, start: monthStartISO, end: monthEndISO });
      return r.data || [];
    },
    [workspaceId, clientId, monthStartISO, monthEndISO],
    { intervalMs: 15_000, refetchOnFocus: true, events: ['stellar-content-posts-changed'], enabled: !!workspaceId && !!clientId, initialData: [], topic: 'content_posts' },
  );

  // D63 — Unread client comments per post (for violet tint on calendar cells).
  // D65 P1-3 — Also pull total comments per post for the "Has comments" filter.
  const { data: commentsSummary, refetch: refetchUnread } = useLiveData(
    async () => {
      if (!workspaceId || !clientId) return { unread_by_post_id: {}, comments_by_post_id: {} };
      try {
        const r = await contentPostAPI.unreadCommentsSummary(workspaceId, clientId);
        return r.data || { unread_by_post_id: {}, comments_by_post_id: {} };
      } catch { return { unread_by_post_id: {}, comments_by_post_id: {} }; }
    },
    [workspaceId, clientId],
    { intervalMs: 15_000, refetchOnFocus: true, events: ['stellar-content-posts-changed', 'stellar-content-comments-changed'], enabled: !!workspaceId && !!clientId, initialData: { unread_by_post_id: {}, comments_by_post_id: {} }, topic: 'content_posts' },
  );
  const unreadByPostId = commentsSummary?.unread_by_post_id || {};
  const commentsByPostId = commentsSummary?.comments_by_post_id || {};

  // Group posts by their LOCAL YYYY-MM-DD.
  const postsByDate = useMemo(() => {
    const m = {};
    (posts || []).forEach(p => {
      if (!p.scheduled_for) return;
      const key = ymdLocal(new Date(p.scheduled_for));
      (m[key] = m[key] || []).push(p);
    });
    return m;
  }, [posts]);

  // D63 — Per-date unread comment count + per-post unread flag.
  const unreadByDate = useMemo(() => {
    const out = {};
    Object.entries(postsByDate).forEach(([key, list]) => {
      out[key] = list.reduce((acc, p) => acc + ((unreadByPostId || {})[p.id] || 0), 0);
    });
    return out;
  }, [postsByDate, unreadByPostId]);

  // Load locations for the active client (used in editor).
  useEffect(() => {
    if (!clientId || !workspaceId) { setLocations([]); return; }
    clientGroupAPI.listLocations(workspaceId, clientId).then(r => setLocations(r.data || [])).catch(() => setLocations([]));
  }, [clientId, workspaceId]);

  const matrix = useMemo(() => buildMonthMatrix(cursor.getFullYear(), cursor.getMonth()), [cursor]);

  const openNewOnDate = (dateObj) => {
    setEditing(null);
    setComments([]);
    setShareUrl(null);
    const iso = `${ymdLocal(dateObj)}T09:00`;
    setForm({ ...EMPTY_POST, client_id: clientId || '', scheduled_for: iso });
    setEditorOpen(true);
  };

  const openEdit = async (p) => {
    setEditing(p);
    setShareUrl(null);
    setForm({
      client_id: p.client_id,
      target_locations: p.target_locations || [],
      scheduled_for: p.scheduled_for ? p.scheduled_for.slice(0, 16) : '',
      platforms: p.platforms || [],
      content_type: p.content_type || 'social_post',
      caption: p.caption || '',
      media: p.media || [],
      status: p.status || 'idea',
      tags: p.tags || [],
      notes: p.notes || '',
    });
    setEditorOpen(true);
    // Load comments + mark all as read for the violet-tint clearing.
    try {
      const r = await contentPostAPI.listComments(workspaceId, p.id);
      setComments(r.data || []);
    } catch { setComments([]); }
    try {
      await contentPostAPI.markCommentsRead(workspaceId, p.id);
      broadcastChange('stellar-content-comments-changed');
      refetchUnread();
    } catch { /* ignore */ }
  };

  const handleSave = useCallback(async (alsoShare = false) => {
    if (!form.client_id) { toast.error('No client selected'); return; }
    setSaving(true);
    try {
      const body = { ...form, scheduled_for: form.scheduled_for ? new Date(form.scheduled_for).toISOString() : null };
      let saved;
      if (editing) {
        const r = await contentPostAPI.update(workspaceId, editing.id, body);
        saved = r.data;
        toast.success('Post updated');
      } else {
        const r = await contentPostAPI.create(workspaceId, body);
        saved = r.data;
        setEditing(saved);
        toast.success('Post created');
      }
      broadcastChange('stellar-content-posts-changed');
      if (alsoShare && saved?.id) {
        const r = await contentPostAPI.share(workspaceId, saved.id);
        const url = r.data?.share_url;
        if (url) {
          setShareUrl(url);
          try { await navigator.clipboard.writeText(url); toast.success('Share link copied to clipboard'); }
          catch { toast.success(`Share link: ${url}`); }
        }
      } else {
        setEditorOpen(false);
      }
      refetch();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  }, [form, editing, workspaceId, refetch]);

  const handleDelete = useCallback(async (p) => {
    try {
      await contentPostAPI.remove(workspaceId, p.id);
      toast.success('Post deleted');
      broadcastChange('stellar-content-posts-changed');
      setConfirmDelete(null);
      refetch();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
  }, [workspaceId, refetch]);

  const handleShareCopy = useCallback(async () => {
    if (!editing?.id) { toast.error('Save the post first'); return; }
    try {
      const r = await contentPostAPI.share(workspaceId, editing.id);
      const url = r.data?.share_url;
      setShareUrl(url);
      await navigator.clipboard.writeText(url);
      toast.success('Share link copied');
    } catch (e) { toast.error('Failed to generate share link'); }
  }, [editing, workspaceId]);

  const handleEmailShare = useCallback(async () => {
    if (!editing?.id) { toast.error('Save the post first'); return; }
    try {
      const r = await contentPostAPI.share(workspaceId, editing.id);
      const url = r.data?.share_url;
      setShareUrl(url);
      const dt = editing.scheduled_for ? new Date(editing.scheduled_for).toLocaleDateString() : 'review';
      const subject = encodeURIComponent(`Review your content for ${dt}`);
      const body = encodeURIComponent(`Hi,\n\nPlease review and approve your scheduled post for ${dt}:\n${url}\n\nYou can leave comments or approve directly from that page.\n\nThanks.`);
      window.location.href = `mailto:?subject=${subject}&body=${body}`;
    } catch (e) { toast.error('Failed to generate share link'); }
  }, [editing, workspaceId]);

  const handleAddInternalComment = useCallback(async () => {
    if (!editing?.id || !commentBody.trim()) return;
    try {
      const r = await contentPostAPI.addComment(workspaceId, editing.id, commentBody.trim());
      setComments(prev => [...prev, r.data]);
      setCommentBody('');
    } catch (e) { toast.error('Failed to add comment'); }
  }, [editing, workspaceId, commentBody]);

  const handleFiles = useCallback(async (files) => {
    if (!files || !files.length) return;
    const remaining = 10 - (form.media || []).length;
    if (remaining <= 0) { toast.error('Max 10 files per post'); return; }
    const arr = Array.from(files).slice(0, remaining);
    setUploading(true);
    try {
      const backendBase = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
      for (const f of arr) {
        if (f.size > 100 * 1024 * 1024) { toast.error(`${f.name} > 100MB`); continue; }
        const r = await mediaAPI.upload(workspaceId, f);
        const m = r.data;
        const isVideo = (m.content_type || '').startsWith('video/');
        // D63 — use the server-issued public_url so the <img>/<video> can fetch
        // without an Authorization header. Fall back to legacy /download path
        // for older media docs that lack public_url.
        const rawUrl = m.public_url || `/api/media/${workspaceId}/${m.id}/download`;
        const url = rawUrl.startsWith('http') ? rawUrl : `${backendBase}${rawUrl}`;
        const thumb = m.thumbnail_url
          ? (m.thumbnail_url.startsWith('http') ? m.thumbnail_url : `${backendBase}${m.thumbnail_url}`)
          : null;
        setForm(prev => ({
          ...prev,
          media: [...(prev.media || []), {
            id: m.id, url, thumbnail_url: thumb, content_type: m.content_type,
            filename: m.filename, size: m.file_size,
            kind: isVideo ? 'video' : 'image',
          }],
        }));
      }
      toast.success('Media uploaded');
    } catch (e) { toast.error('Upload failed'); }
    finally { setUploading(false); }
  }, [workspaceId, form.media]);

  const removeMedia = (mediaId) => setForm(f => ({ ...f, media: (f.media || []).filter(m => m.id !== mediaId) }));

  // ── D62 — Send Calendar to Client state ─────────────────────────────
  const [sendCalOpen, setSendCalOpen] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [sendForm, setSendForm] = useState({ name: '', email: '', phone: '', message: '', save_contact: true, contact_id: null });
  const [sending, setSending] = useState(false);
  const [lastShareUrl, setLastShareUrl] = useState(null);
  const [recentShares, setRecentShares] = useState([]);
  const [resendingId, setResendingId] = useState(null);

  const loadContacts = useCallback(async () => {
    if (!clientId || !workspaceId) return;
    try {
      const r = await clientContactAPI.list(workspaceId, clientId);
      setContacts(r.data || []);
    } catch { setContacts([]); }
  }, [workspaceId, clientId]);

  const loadRecentShares = useCallback(async () => {
    if (!clientId || !workspaceId) return;
    try {
      const r = await calendarShareAPI.listForClient(workspaceId, clientId);
      setRecentShares(r.data || []);
    } catch { setRecentShares([]); }
  }, [workspaceId, clientId]);

  useEffect(() => { loadContacts(); }, [loadContacts]);

  const openSendCalendar = () => {
    setSendForm({ name: '', email: '', phone: '', message: '', save_contact: true, contact_id: null });
    setLastShareUrl(null);
    setSendCalOpen(true);
    loadRecentShares();
  };

  const handleResendShare = useCallback(async (shareId) => {
    setResendingId(shareId);
    try {
      const r = await calendarShareAPI.resend(workspaceId, shareId);
      if (r.data?.email_sent) {
        toast.success(`Re-sent to ${r.data.recipient_name}`);
      } else {
        try { await navigator.clipboard.writeText(r.data?.share_url || ''); } catch {}
        toast.warning('Email failed — link copied', { description: r.data?.email_error || '' });
      }
      await loadRecentShares();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Re-send failed');
    } finally {
      setResendingId(null);
    }
  }, [workspaceId, loadRecentShares]);

  const pickContact = (cid) => {
    if (!cid) { setSendForm(f => ({ ...f, name: '', email: '', phone: '', contact_id: null })); return; }
    const c = contacts.find(x => x.id === cid);
    if (c) setSendForm(f => ({ ...f, name: c.name || '', email: c.email || '', phone: c.phone || '', contact_id: c.id, save_contact: false }));
  };

  const handleSendCalendar = useCallback(async () => {
    if (!sendForm.name.trim() || !sendForm.email.trim()) { toast.error('Recipient name + email required'); return; }
    setSending(true);
    try {
      const monthStr = `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}`;
      const r = await calendarShareAPI.create(workspaceId, {
        client_id: clientId, month: monthStr,
        recipient_name: sendForm.name.trim(),
        recipient_email: sendForm.email.trim(),
        recipient_phone: sendForm.phone.trim(),
        personal_message: sendForm.message,
        send_email: true,
        save_contact: sendForm.save_contact,
        contact_id: sendForm.contact_id,
      });
      const url = r.data?.share_url;
      setLastShareUrl(url);
      if (r.data?.email_sent) {
        toast.success(`Calendar sent to ${sendForm.name.trim()}`);
      } else {
        try { await navigator.clipboard.writeText(url); } catch {}
        toast.warning(`Email failed — link copied to clipboard`, { description: r.data?.email_error || '' });
      }
      await loadContacts();
      await loadRecentShares();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to send');
    } finally {
      setSending(false);
    }
  }, [sendForm, cursor, workspaceId, clientId, loadContacts]);
  // ────────────────────────────────────────────────────────────────────

  if (!clientId) {
    return <div className="p-6 text-[12px] text-muted-foreground/60">Select a client to view its content calendar.</div>;
  }

  const todayKey = ymdLocal(today);

  return (
    <div className="space-y-3" data-testid="content-calendar">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-1.5">
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))} aria-label="Previous month" data-testid="cal-prev-month"><ChevronLeft className="h-4 w-4" /></Button>
          <h2 className="text-[15px] font-semibold text-foreground min-w-[160px] text-center" aria-live="polite" data-testid="cal-month-title">{MONTH_NAMES[cursor.getMonth()]} {cursor.getFullYear()}</h2>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))} aria-label="Next month" data-testid="cal-next-month"><ChevronRight className="h-4 w-4" /></Button>
          <Button variant="ghost" size="sm" className="text-[11px] h-7" onClick={() => setCursor(new Date(today.getFullYear(), today.getMonth(), 1))} aria-label="Jump to today" data-testid="cal-today-btn">Today</Button>
        </div>
        <div className="flex items-center gap-2">
          <LastSyncedPill lastSynced={lastSynced} loading={loading} onRefetch={refetch} />
          {/* D64 — "Has comments" filter chip — sticky per session. */}
          <button
            type="button"
            onClick={() => setHasCommentsFilter(v => !v)}
            className={`text-[11px] h-8 px-3 rounded-md border transition-colors inline-flex items-center gap-1.5 ${hasCommentsFilter ? 'bg-violet-500/15 border-violet-500/40 text-violet-300' : 'border-border/30 text-muted-foreground/70 hover:border-border/50'}`}
            data-testid="cal-has-comments-chip"
            title="Show only posts with client comments"
          >
            <MessageCircle className="h-3 w-3" /> Has comments
          </button>
          <Button size="sm" variant="outline" onClick={openSendCalendar} className="h-8 text-[12px] border-gold/30 text-gold hover:bg-gold/10" data-testid="send-calendar-btn">
            <Mail className="h-3.5 w-3.5 mr-1.5" /> Send to client
          </Button>
          <Button size="sm" onClick={() => openNewOnDate(today)} className="h-8 text-[12px] bg-gold/15 hover:bg-gold/25 text-gold border-0" data-testid="add-content-post-btn">
            <Plus className="h-3.5 w-3.5 mr-1.5" /> New Post
          </Button>
        </div>
      </div>

      {/* Calendar card — matches Deadline Calendar styling */}
      <Card className="card-hover-gold hidden md:block"><CardContent className="p-2 overflow-x-auto">
        <div className="min-w-[640px] sm:min-w-0">
          <div className="grid grid-cols-7 mb-1">
            {DAY_LABELS.map(d => <div key={d} className="text-center text-[11px] font-semibold text-muted-foreground/50 py-2">{d}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-1" data-testid="cal-month-grid">
            {matrix.flat().map((d, idx) => {
              const inMonth = d.getMonth() === cursor.getMonth();
              const key = ymdLocal(d);
              const dayPostsAll = postsByDate[key] || [];
              const dayPosts = hasCommentsFilter
                ? dayPostsAll.filter(p => ((unreadByPostId || {})[p.id] || 0) > 0)
                : dayPostsAll;
              const isToday = key === todayKey;
              // Mon=0..Sun=6 in our matrix.
              const dayOfWeek = idx % 7;
              const isWeekend = dayOfWeek === 5 || dayOfWeek === 6; // Sat / Sun
              const unreadCount = unreadByDate[key] || 0;
              const hasUnreadComments = unreadCount > 0;
              const cellBg = hasUnreadComments
                ? 'border-violet-500/40 bg-violet-500/[0.10] hover:bg-violet-500/[0.14]'
                : isToday
                  ? 'border-gold/40 bg-gold/[0.06] ring-1 ring-gold/20'
                  : isWeekend
                    ? 'border-transparent bg-foreground/[0.025] hover:bg-foreground/[0.04]'
                    : 'border-transparent bg-foreground/[0.01] hover:bg-foreground/[0.03]';
              return (
                <div
                  key={idx}
                  role="gridcell"
                  tabIndex={0}
                  aria-label={`${key}${isToday ? ', today' : ''}${dayPosts.length ? `, ${dayPosts.length} post${dayPosts.length > 1 ? 's' : ''}` : ''}${hasUnreadComments ? `, ${unreadCount} unread client comment${unreadCount > 1 ? 's' : ''}` : ''}`}
                  title={hasUnreadComments ? `💬 ${unreadCount} comment${unreadCount > 1 ? 's' : ''} from client` : undefined}
                  className={`min-h-[100px] rounded-lg p-1 border cursor-pointer transition-colors group relative ${cellBg} ${inMonth ? '' : 'opacity-50'}`}
                  onClick={() => openNewOnDate(d)}
                  data-testid={`cal-cell-${key}`}
                  data-has-unread-comments={hasUnreadComments ? '1' : '0'}
                >
                  {hasUnreadComments && (
                    <span className="absolute top-1 right-1 h-2 w-2 rounded-full bg-violet-400 ring-2 ring-violet-500/30" data-testid={`cal-cell-comment-dot-${key}`} />
                  )}
                  <div className="flex items-center justify-between px-0.5">
                    <span className={`text-[11px] font-semibold ${isToday ? 'text-gold' : hasUnreadComments ? 'text-violet-300' : isWeekend && inMonth ? 'text-muted-foreground/60' : inMonth ? 'text-foreground/70' : 'text-muted-foreground/30'}`}>{d.getDate()}</span>
                    {isToday && !hasUnreadComments && <span className="text-[8px] text-gold/70 font-semibold">TODAY</span>}
                    {!isToday && !hasUnreadComments && (
                      <button
                        onClick={(e) => { e.stopPropagation(); openNewOnDate(d); }}
                        className="opacity-0 group-hover:opacity-100 text-[10px] text-gold/70 hover:text-gold px-1.5 rounded hover:bg-gold/10 transition-opacity inline-flex items-center gap-0.5"
                        title={`Create post for ${key}`}
                        data-testid={`cal-new-post-${key}`}
                        aria-label={`Create post for ${key}`}
                      >
                        + New
                      </button>
                    )}
                  </div>
                  <div className="mt-0.5 space-y-0.5">
                    {dayPosts.slice(0, 3).map(p => {
                      const postUnread = (unreadByPostId || {})[p.id] || 0;
                      return (
                        <button
                          key={p.id}
                          onClick={(e) => { e.stopPropagation(); openEdit(p); }}
                          className={`flex items-center gap-1.5 w-full p-1 rounded border-l-2 text-left hover:bg-foreground/[0.04] transition-all ${STATUS_COLORS[p.status] || STATUS_COLORS.draft}`}
                          data-testid={`cal-chip-${p.id}`}
                          title={p.caption || '(no caption)'}
                        >
                          {postUnread > 0 ? <MessageCircle className="h-2.5 w-2.5 shrink-0 text-violet-400" /> : (p.media?.[0]?.kind === 'video' ? <Film className="h-2.5 w-2.5 shrink-0" /> : p.media?.[0] ? <ImgIcon className="h-2.5 w-2.5 shrink-0" /> : <span className="h-1.5 w-1.5 rounded-full bg-current shrink-0 opacity-60" />)}
                          <span className="text-[11px] font-medium truncate flex-1">{p.caption || '(no caption)'}</span>
                          {p.client_approved && <CheckCircle2 className="h-2.5 w-2.5 text-emerald-400 shrink-0" />}
                        </button>
                      );
                    })}
                    {dayPosts.length > 3 && (
                      <button
                        onClick={(e) => { e.stopPropagation(); /* future side panel */ }}
                        className="text-[8px] text-gold/70 hover:text-gold px-1 underline-offset-2 hover:underline"
                        data-testid={`cal-overflow-${key}`}
                      >
                        +{dayPosts.length - 3} more
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </CardContent></Card>

      {/* Mobile: vertical day list (only show days w/ posts + today) */}
      <div className="md:hidden space-y-2">
        {matrix.flat().filter(d => d.getMonth() === cursor.getMonth()).map(d => {
          const key = ymdLocal(d);
          const dayPosts = postsByDate[key] || [];
          const isToday = key === todayKey;
          if (dayPosts.length === 0 && !isToday) return null;
          return (
            <Card key={key} className={`bg-card border-border/30 ${isToday ? 'ring-1 ring-gold/40' : ''}`}>
              <CardContent className="p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[12px] font-medium">{d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}</div>
                  <Button size="sm" variant="outline" onClick={() => openNewOnDate(d)} className="h-6 text-[10px] border-border/30"><Plus className="h-2.5 w-2.5 mr-0.5" /> Add</Button>
                </div>
                <div className="space-y-1">
                  {dayPosts.map(p => (
                    <div key={p.id} onClick={() => openEdit(p)} className={`text-[11px] px-2 py-1.5 rounded border cursor-pointer ${STATUS_COLORS[p.status] || STATUS_COLORS.draft}`} data-testid={`cal-chip-${p.id}`}>
                      <div className="truncate">{p.caption || '(no caption)'}</div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Editor drawer */}
      <Dialog open={editorOpen} onOpenChange={(o) => { if (!saving) setEditorOpen(o); }}>
        <DialogContent className="sm:max-w-[680px] max-h-[90vh] overflow-y-auto" data-testid="content-post-editor">
          <DialogHeader>
            <DialogTitle className="font-display text-[16px] flex items-center gap-2">
              {editing ? 'Edit Post' : 'New Post'}
              {editing?.client_approved && <span className="text-[10px] px-2 py-0.5 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> Client approved</span>}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {/* Media upload */}
            <FormField label="Media">
              <div
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); handleFiles(e.dataTransfer.files); }}
                className="border border-dashed border-border/40 rounded-md p-3 bg-foreground/[0.02]"
                data-testid="post-media-dropzone"
              >
                {(form.media || []).length > 0 && (
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mb-2">
                    {form.media.map((m) => {
                      const isBroken = brokenMedia.has(m.id);
                      return (
                      <div key={m.id} className={`relative group aspect-square rounded overflow-hidden bg-foreground/[0.05] border ${isBroken ? 'border-rose-500/40' : 'border-border/30'}`} data-testid={`post-media-thumb-${m.id}`}>
                        {m.kind === 'video' ? (
                          <video src={m.url} poster={m.thumbnail_url || undefined} controls preload="metadata" className="w-full h-full object-cover bg-black" onError={() => markBroken(m.id)} />
                        ) : (
                          <img src={m.url} alt={m.filename || ''} loading="lazy" className={`w-full h-full object-cover ${isBroken ? 'opacity-30' : ''}`} onError={() => markBroken(m.id)} />
                        )}
                        {/* D64 — Download/Remove always visible (was opacity-0 group-hover only). */}
                        <div className="absolute top-1 right-1 flex gap-1 opacity-80 hover:opacity-100 transition-opacity">
                          <a href={m.url} download={m.filename || true} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} className="bg-black/70 rounded p-1 text-white hover:bg-black/90 ring-1 ring-white/10" title="Download" data-testid={`post-media-download-${m.id}`}>
                            <Upload className="h-3 w-3 rotate-180" />
                          </a>
                          <button type="button" onClick={() => removeMedia(m.id)} className="bg-black/70 rounded p-1 text-white hover:bg-red-500/80 ring-1 ring-white/10" title="Remove" data-testid={`post-media-remove-${m.id}`}>
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                        {/* D64 — Broken indicator. Pure React state, no fragile CSS selectors. */}
                        {isBroken && (
                          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                            <span className="text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-rose-500/30 text-rose-200 border border-rose-500/40" data-testid={`post-media-broken-${m.id}`}>⚠ Broken</span>
                          </div>
                        )}
                        {m.filename && (
                          <div className="absolute inset-x-0 bottom-0 px-1 py-0.5 bg-gradient-to-t from-black/80 to-transparent text-[9px] text-white/80 truncate">{m.filename}</div>
                        )}
                      </div>
                      );
                    })}
                  </div>
                )}
                <input ref={fileInputRef} type="file" multiple accept="image/*,video/*" className="hidden" onChange={(e) => handleFiles(e.target.files)} data-testid="post-media-input" />
                <Button type="button" size="sm" variant="outline" onClick={() => fileInputRef.current?.click()} disabled={uploading} className="h-8 text-[11px] border-border/30" data-testid="post-media-add-btn">
                  {uploading ? <Loader2 className="h-3 w-3 mr-1.5 animate-spin" /> : <Upload className="h-3 w-3 mr-1.5" />}
                  Add media (img / video, max 100MB)
                </Button>
              </div>
            </FormField>

            <FormField label="Caption">
              <Textarea value={form.caption} onChange={e => setForm(f => ({ ...f, caption: e.target.value }))} rows={4} placeholder="Write the post caption…" data-testid="post-caption-input" />
            </FormField>

            <div className="grid grid-cols-2 gap-3">
              <FormField label="Scheduled time">
                <Input type="datetime-local" value={form.scheduled_for} onChange={e => setForm(f => ({ ...f, scheduled_for: e.target.value }))} data-testid="post-schedule-input" />
              </FormField>
              <FormField label="Status">
                <select className="w-full h-9 px-3 text-[13px] bg-card border border-border/30 rounded-md text-foreground focus:border-gold/50 focus:outline-none" value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))} data-testid="post-status-input">
                  {STATUSES.map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
                </select>
              </FormField>
            </div>

            <FormField label="Content type">
              <select className="w-full h-9 px-3 text-[13px] bg-card border border-border/30 rounded-md text-foreground focus:border-gold/50 focus:outline-none" value={form.content_type} onChange={e => setForm(f => ({ ...f, content_type: e.target.value }))} data-testid="post-type-input">
                {TYPES.map(t => <option key={t} value={t}>{t.replace('_', ' ')}</option>)}
              </select>
            </FormField>

            <FormField label="Platforms">
              <div className="flex flex-wrap gap-1.5" data-testid="post-platforms-chips">
                {PLATFORMS.map(p => {
                  const on = (form.platforms || []).includes(p);
                  return (
                    <button key={p} type="button"
                            title={PLATFORM_LABELS[p]}
                            onClick={() => setForm(f => ({ ...f, platforms: on ? f.platforms.filter(x => x !== p) : [...f.platforms, p] }))}
                            className={`text-[11px] px-2 py-1 rounded border ${on ? 'bg-gold/15 text-gold border-gold/30' : 'bg-foreground/[0.03] text-muted-foreground/60 border-border/30 hover:bg-foreground/[0.06]'}`}
                            data-testid={`post-platform-${p}`}>
                      {p === 'GMB' ? <span className="inline-flex items-center gap-1"><Globe className="h-3 w-3" /> GMB</span> : p}
                    </button>
                  );
                })}
              </div>
            </FormField>

            {locations.length > 0 && (
              <FormField label="Target locations (empty = brand-level / all)">
                <div className="flex items-center gap-1.5 mb-1.5">
                  <Button type="button" size="sm" variant="outline" onClick={() => setForm(f => ({ ...f, target_locations: locations.map(l => l.id) }))} className="h-6 text-[10px] border-border/30" data-testid="post-locations-all">Select all</Button>
                  <Button type="button" size="sm" variant="outline" onClick={() => setForm(f => ({ ...f, target_locations: [] }))} className="h-6 text-[10px] border-border/30">Clear</Button>
                </div>
                <div className="flex flex-wrap gap-1.5" data-testid="post-locations-chips">
                  {locations.map(loc => {
                    const on = (form.target_locations || []).includes(loc.id);
                    return (
                      <button key={loc.id} type="button"
                              onClick={() => setForm(f => ({ ...f, target_locations: on ? f.target_locations.filter(x => x !== loc.id) : [...f.target_locations, loc.id] }))}
                              className={`text-[11px] px-2 py-1 rounded border ${on ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' : 'bg-foreground/[0.03] text-muted-foreground/60 border-border/30'}`}
                              data-testid={`post-location-${loc.id}`}>
                        <MapPin className="h-3 w-3 inline mr-1" />{loc.location_name}
                      </button>
                    );
                  })}
                </div>
              </FormField>
            )}

            <FormField label="Tags (comma-separated)">
              <Input value={(form.tags || []).join(', ')} onChange={e => setForm(f => ({ ...f, tags: e.target.value.split(',').map(s => s.trim()).filter(Boolean) }))} placeholder="launch, valentines, q1" data-testid="post-tags-input" />
            </FormField>

            <FormField label="Internal notes">
              <Textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2} data-testid="post-notes-input" />
            </FormField>

            {/* Approval flag (manual / internal) */}
            <label className="flex items-center gap-2 text-[12px] text-foreground/80 cursor-pointer">
              <input type="checkbox" checked={form.status === 'approved'} onChange={(e) => setForm(f => ({ ...f, status: e.target.checked ? 'approved' : (f.status === 'approved' ? 'draft' : f.status) }))} data-testid="post-approved-checkbox" />
              Mark as approved
            </label>

            {/* Share URL pill */}
            {shareUrl && (
              <div className="text-[11px] bg-gold/5 border border-gold/20 rounded-md p-2 flex items-center gap-2" data-testid="share-url-pill">
                <Share2 className="h-3 w-3 text-gold" />
                <span className="text-gold/90 truncate flex-1">{shareUrl}</span>
                <button type="button" onClick={async () => { await navigator.clipboard.writeText(shareUrl); toast.success('Copied'); }} className="text-gold hover:text-gold/80" data-testid="share-url-copy"><CopyIcon className="h-3 w-3" /></button>
              </div>
            )}

            {/* Comments thread (only for existing posts) */}
            {editing && (
              <div className="border-t border-border/20 pt-3" data-testid="comments-section">
                <div className="flex items-center gap-1.5 mb-2 text-[12px] text-foreground/80"><MessageCircle className="h-3.5 w-3.5" /> Comments ({comments.length})</div>
                <div className="space-y-2 max-h-[200px] overflow-y-auto mb-2">
                  {comments.length === 0 ? (
                    <div className="text-[11px] text-muted-foreground/50 italic">No comments yet.</div>
                  ) : comments.map(c => (
                    <div key={c.id} className="text-[11px] bg-foreground/[0.03] border border-border/20 rounded p-2" data-testid={`comment-${c.id}`}>
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <span className={`text-[9px] px-1.5 py-0.5 rounded ${c.author_type === 'external_client' ? 'bg-blue-500/15 text-blue-300' : 'bg-gold/15 text-gold'}`}>
                          {c.author_type === 'external_client' ? '🌐 External Client' : 'Internal'}
                        </span>
                        <span className="font-medium text-foreground/90">{c.author_name}</span>
                        <span className="text-muted-foreground/40">{new Date(c.created_at).toLocaleString()}</span>
                      </div>
                      <div className="text-foreground/80 whitespace-pre-wrap">{c.body}</div>
                    </div>
                  ))}
                </div>
                <div className="flex gap-1.5">
                  <Textarea value={commentBody} onChange={e => setCommentBody(e.target.value)} rows={2} placeholder="Add internal comment…" className="text-[12px]" data-testid="comment-body-input" />
                  <Button type="button" size="sm" onClick={handleAddInternalComment} disabled={!commentBody.trim()} className="h-9 self-end bg-gold/15 hover:bg-gold/25 text-gold border-0" data-testid="comment-send-btn"><Send className="h-3 w-3" /></Button>
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={() => setEditorOpen(false)} disabled={saving} className="text-[12px] h-8 border-border/30" data-testid="post-cancel">Cancel</Button>
            {editing && (
              <>
                <Button variant="outline" size="sm" onClick={() => setConfirmDelete(editing)} className="text-[12px] h-8 border-red-500/30 text-red-300 hover:bg-red-500/10" data-testid="post-delete">
                  <Trash2 className="h-3 w-3 mr-1" /> Delete
                </Button>
                <Button variant="outline" size="sm" onClick={handleShareCopy} disabled={saving} className="text-[12px] h-8 border-border/30" data-testid="post-share-copy"><Share2 className="h-3 w-3 mr-1" /> Copy share link</Button>
                <Button variant="outline" size="sm" onClick={handleEmailShare} disabled={saving} className="text-[12px] h-8 border-border/30" data-testid="post-share-email">Email link</Button>
              </>
            )}
            <Button size="sm" onClick={() => handleSave(false)} disabled={saving} className="text-[12px] h-8 bg-gold/15 hover:bg-gold/25 text-gold border-0" data-testid="post-save">
              {saving ? <Loader2 className="h-3 w-3 mr-1.5 animate-spin" /> : null}
              {editing ? 'Save' : 'Save as draft'}
            </Button>
            <Button size="sm" onClick={() => handleSave(true)} disabled={saving} className="text-[12px] h-8 bg-gold hover:bg-gold/90 text-background border-0" data-testid="post-save-and-share">
              {saving ? <Loader2 className="h-3 w-3 mr-1.5 animate-spin" /> : <Share2 className="h-3 w-3 mr-1" />}
              Save &amp; Send to client
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <DialogContent className="sm:max-w-[400px]" data-testid="post-delete-confirm">
          <DialogHeader>
            <DialogTitle className="font-display text-[16px] flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-red-400" /> Delete post?</DialogTitle>
          </DialogHeader>
          <p className="text-[12px] text-muted-foreground/70 py-2 line-clamp-3">{confirmDelete?.caption || '(no caption)'}</p>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setConfirmDelete(null)} className="text-[12px] h-8 border-border/30">Cancel</Button>
            <Button size="sm" onClick={() => { handleDelete(confirmDelete); setEditorOpen(false); }} className="text-[12px] h-8 bg-red-500/15 hover:bg-red-500/25 text-red-300 border-0" data-testid="post-delete-confirm-btn">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* D62 — Send Calendar to Client */}
      <Dialog open={sendCalOpen} onOpenChange={(o) => { if (!sending) setSendCalOpen(o); }}>
        <DialogContent className="sm:max-w-[560px] max-h-[90vh] overflow-y-auto" data-testid="send-calendar-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-[16px] flex items-center gap-2"><Mail className="h-4 w-4 text-gold" /> Send {MONTH_NAMES[cursor.getMonth()]} {cursor.getFullYear()} calendar to client</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {recentShares.length > 0 && (
              <div className="space-y-1.5" data-testid="recent-shares-panel">
                <Label className="text-[11px] uppercase tracking-wide text-muted-foreground/60 flex items-center gap-1"><Clock className="h-3 w-3" /> Previously sent</Label>
                <div className="space-y-1 max-h-[120px] overflow-y-auto rounded-md border border-border/20 bg-foreground/[0.02] p-1">
                  {recentShares.slice(0, 6).map(s => {
                    const yr = s.month?.split('-')[0];
                    const mo = parseInt((s.month || '').split('-')[1] || '1', 10) - 1;
                    const monthLabel = `${MONTH_NAMES[mo] || ''} ${yr || ''}`;
                    const sent = new Date(s.last_resent_at || s.sent_at).toLocaleDateString();
                    return (
                      <div key={s.id} className="flex items-center gap-2 p-1.5 rounded hover:bg-foreground/[0.04] group" data-testid={`recent-share-${s.id}`}>
                        <div className="flex-1 min-w-0">
                          <div className="text-[11px] font-medium text-foreground/85 truncate">
                            {s.recipient_name}
                            <span className="text-muted-foreground/60 font-normal"> · {s.recipient_email}</span>
                          </div>
                          <div className="text-[10px] text-muted-foreground/50 flex items-center gap-1.5">
                            <span>{monthLabel}</span><span>·</span><span>sent {sent}</span>
                            {(s.resent_count || 0) > 0 && <span className="px-1 rounded bg-foreground/[0.06] text-[9px]">×{s.resent_count + 1}</span>}
                            {s.view_count > 0 && <span className="text-emerald-400/70 text-[9px]">viewed {s.view_count}×</span>}
                          </div>
                        </div>
                        <Button
                          type="button" size="sm" variant="outline"
                          onClick={() => handleResendShare(s.id)}
                          disabled={resendingId === s.id}
                          className="h-7 text-[10px] border-border/30 opacity-60 group-hover:opacity-100 transition-opacity"
                          data-testid={`recent-share-resend-${s.id}`}
                          title={`Re-send ${monthLabel} to ${s.recipient_name}`}
                        >
                          {resendingId === s.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <><RotateCw className="h-3 w-3 mr-1" /> Re-send</>}
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {contacts.length > 0 && (
              <FormField label={<span className="inline-flex items-center gap-1"><Users className="h-3 w-3" /> Pick a saved contact</span>}>
                <select
                  className="w-full h-9 px-3 text-[13px] bg-card border border-border/30 rounded-md text-foreground focus:border-gold/50 focus:outline-none"
                  value={sendForm.contact_id || ''}
                  onChange={e => pickContact(e.target.value)}
                  data-testid="send-cal-contact-picker"
                >
                  <option value="">— Enter new contact —</option>
                  {contacts.map(c => (
                    <option key={c.id} value={c.id}>{c.name}{c.email ? ` · ${c.email}` : ''}{c.role ? ` (${c.role})` : ''}</option>
                  ))}
                </select>
              </FormField>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <FormField label="Recipient name">
                <Input value={sendForm.name} onChange={e => setSendForm(f => ({ ...f, name: e.target.value }))} placeholder="Jane Owner" data-testid="send-cal-name" />
              </FormField>
              <FormField label="Recipient email">
                <Input type="email" value={sendForm.email} onChange={e => setSendForm(f => ({ ...f, email: e.target.value }))} placeholder="jane@client.com" data-testid="send-cal-email" />
              </FormField>
            </div>
            <FormField label="Phone (optional)">
              <Input value={sendForm.phone} onChange={e => setSendForm(f => ({ ...f, phone: e.target.value }))} placeholder="+1 555 123 4567" data-testid="send-cal-phone" />
            </FormField>
            <FormField label="Personal message">
              <Textarea
                value={sendForm.message}
                onChange={e => setSendForm(f => ({ ...f, message: e.target.value }))}
                rows={3}
                placeholder={`Hi ${sendForm.name || '<name>'}, please review your scheduled content for ${MONTH_NAMES[cursor.getMonth()]} ${cursor.getFullYear()}. Comment or approve directly on the page.`}
                data-testid="send-cal-message"
              />
            </FormField>
            <label className="flex items-center gap-2 text-[12px] text-foreground/80 cursor-pointer">
              <input type="checkbox" checked={sendForm.save_contact} onChange={e => setSendForm(f => ({ ...f, save_contact: e.target.checked }))} data-testid="send-cal-save-contact" />
              <UserPlus className="h-3.5 w-3.5" /> Save this contact for future shares
            </label>
            {lastShareUrl && (
              <div className="text-[11px] bg-gold/5 border border-gold/20 rounded-md p-2 flex items-center gap-2" data-testid="send-cal-result">
                <Share2 className="h-3 w-3 text-gold" />
                <span className="text-gold/90 truncate flex-1">{lastShareUrl}</span>
                <button type="button" onClick={async () => { await navigator.clipboard.writeText(lastShareUrl); toast.success('Copied'); }} className="text-gold hover:text-gold/80" data-testid="send-cal-copy"><CopyIcon className="h-3 w-3" /></button>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setSendCalOpen(false)} disabled={sending} className="text-[12px] h-8 border-border/30">Cancel</Button>
            <Button size="sm" onClick={handleSendCalendar} disabled={sending || !sendForm.name.trim() || !sendForm.email.trim()} className="text-[12px] h-8 bg-gold hover:bg-gold/90 text-background border-0" data-testid="send-cal-submit">
              {sending ? <Loader2 className="h-3 w-3 mr-1.5 animate-spin" /> : <Send className="h-3 w-3 mr-1.5" />}
              Send Calendar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FormField({ label, children }) {
  return (
    <div className="space-y-1">
      <Label className="text-[11px] uppercase tracking-wide text-muted-foreground/60">{label}</Label>
      {children}
    </div>
  );
}
